import './lohn.css';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtDate, fmtDayMonth } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { entryPill } from './entry';
import type { NewEmployeesResponse } from './types';

// Hinweisleisten für Lohn-Zuständige und Admin: „Neuer Mitarbeiter gemeldet“ (auch Änderungen zu Mitarbeitern) und
// „Kündigung gemeldet“. Ein Eintrag bleibt stehen, bis die Lohnbuchhaltung ihn in der Personalakte auf „Erledigt“
// setzt. Ohne clientId alle Mandanten, sonst nur dieser.
export function LohnBar({ clientId }: { clientId?: string }) {
  const navigate = useNavigate();
  const res = useApi<NewEmployeesResponse>(`/api/kanzlei/lohn/new${clientId ? `?clientId=${clientId}` : ''}`, { live: ['lohn'] });
  const d = res.data;
  if (!d) return null;

  const open = (r: { clientId: string; employeeId: string }) =>
    navigate(`/kanzlei/mandanten/${r.clientId}/lohn?ma=${r.employeeId}`);
  // Mandant (bei allen Mandanten) und für den Admin, wer für den Lohn zuständig ist
  const who = (r: { clientName: string; lohn?: string | null }) => (
    <>
      {!clientId && ` · ${r.clientName}`}
      {d.admin && r.lohn !== undefined && (
        <span className={`note${r.lohn ? '' : ' who-open'}`}> · Lohn: {r.lohn ?? 'offen, bitte zuweisen'}</span>
      )}
    </>
  );
  const by = (r: { createdAt: string; createdBy: string | null }) =>
    `gemeldet am ${fmtDayMonth(r.createdAt)}${r.createdBy ? ` von ${r.createdBy}` : ''}`;

  const meldungen: Row[] = [
    ...d.items.map((e) => {
      const pill = entryPill(e.entryDate);
      return {
        key: e.id,
        go: () => open({ clientId: e.clientId, employeeId: e.id }),
        tx: (
          <>
            <b>{e.name}</b>
            {who(e)} <span className={`pill p-${pill.tone}`}>{pill.label}</span>
            <small>{by(e)} · Zu tun: Anmeldung zur Sozialversicherung, Lohnkonto anlegen, ELStAM abrufen</small>
          </>
        ),
      };
    }),
    ...d.changes.map((c) => ({
      key: c.id,
      go: () => open(c),
      tx: (
        <>
          <b>{c.name}</b>
          {who(c)} <span className="pill p-info">Änderung</span>
          <small>
            {c.what}: {c.value}
            {c.validFrom ? ` · gültig ab ${fmtDate(c.validFrom)}` : ''} · {by(c)}
          </small>
        </>
      ),
    })),
  ];
  const neu = d.items.length;
  const head =
    meldungen.length === 1
      ? neu
        ? 'Neuer Mitarbeiter gemeldet'
        : 'Änderung bei einem Mitarbeiter'
      : neu === meldungen.length
        ? `${neu} neue Mitarbeiter gemeldet`
        : `${meldungen.length} Meldungen zu Mitarbeitern`;

  const kuendigungen: Row[] = d.terminations.map((t) => ({
    key: t.id,
    go: () => open(t),
    tx: (
      <>
        <b>{t.name}</b>
        {who(t)} <span className="pill p-warn">letzter Arbeitstag {fmtDate(t.lastDay)}</span>
        <small>
          {t.by} · {by(t)} · Zu tun: Abmeldung zur Sozialversicherung, letzte Lohnabrechnung, Arbeitsbescheinigung
        </small>
      </>
    ),
  }));

  return (
    <>
      {meldungen.length > 0 && <Bar head={`👤 ${head}`} rows={meldungen} />}
      {kuendigungen.length > 0 && (
        <Bar
          head={`👤 ${kuendigungen.length === 1 ? 'Kündigung gemeldet' : `${kuendigungen.length} Kündigungen gemeldet`}`}
          rows={kuendigungen}
        />
      )}
    </>
  );
}

export interface Row {
  key: string;
  tx: ReactNode;
  go(): void;
}

// Eine Leiste; ab 5 Einträgen erst 3 und „Alle anzeigen“ (auch für die Briefe)
export function Bar({ head, rows, className = 'lohnbar' }: { head: string; rows: Row[]; className?: string }) {
  const [all, setAll] = useState(false);
  const cut = rows.length > 4 && !all;
  return (
    <div className={`msgbar ${className}`} role="status">
      <div className="mb-h">{head}</div>
      {(cut ? rows.slice(0, 3) : rows).map((r) => (
        <div className="mb-r" key={r.key}>
          <div className="tx">{r.tx}</div>
          <button type="button" className="btn sm dark" onClick={r.go}>
            Ansehen
          </button>
        </div>
      ))}
      {rows.length > 4 && (
        <button type="button" className="btn sm mb-more" aria-expanded={!cut} onClick={() => setAll((x) => !x)}>
          {cut ? `Alle anzeigen (${rows.length})` : 'Weniger anzeigen'}
        </button>
      )}
    </div>
  );
}
